import { useCallback, useContext, useEffect, useReducer, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { ApiHelper, RmaApiHelper } from "api/ApiHelper";
import { AppContext } from "App";

// State-specific Female Index Constants
const FEMALE_INDEX_BY_STATE = {
  Arkansas: 54,
  Delaware: 45,
  Georgia: 33,
  Illinois: 30,
  Indiana: 43,
  Iowa: 45,
  Kansas: 25,
  Kentucky: 32,
  Maryland: 45,
  Michigan: 40,
  Mississippi: 54,
  Missouri: 56,
  Nebraska: 35,
  "New Jersey": 2,
  "New York": 2,
  "North Carolina": 45,
  "North Dakota": 14,
  Ohio: 33,
  Oklahoma: 2,
  Ontario: 15,
  "South Dakota": 22,
  "West Virginia": 0,
  Wisconsin: 23,
};

// Validation Constants
export const VALIDATION_CONSTANTS = {
  YIELD_POTENTIAL: { min: 0, max: 100 },
  PROJECTED_PRICE: { min: 0, max: 100 },
  FEMALE_INDEX: { min: 0, max: 100 },
  SCN_EGG_COUNT: { min: 0, max: 150000 },
  AVERAGE_SAND: { min: 0, max: 100 },
  AVERAGE_SOIL_PH: { min: 0, max: 14 },
};

// LocalStorage keys
const STORAGE_KEY = "soybean_scn_calculator_settings";
const SAVED_FIELDS = [
  "state_name",
  "county_name",
  "type_name",
  "practice_name",
  "scn_egg_count",
  "map_unit_name",
  "yield_potential",
  "projected_price",
  "female_index",
  "avg_sand",
  "avg_soil_ph",
];

// Reducer for state management
const propUpdateReducer = (state, action) => {
  switch (action.type) {
    case "update":
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };
    case "updateMany":
      return {
        ...state,
        ...action.payload,
      };
    case "replace":
      return {
        ...action.payload,
      };
    default:
      return state;
  }
};

export const useSoybeanScnCalculator = (initialOverrides = {}) => {
  const { globalState } = useContext(AppContext);

  // Helper functions for localStorage
  const saveToLocalStorage = useCallback((fieldsToSave) => {
    try {
      const existingData = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      const updatedData = { ...existingData, ...fieldsToSave };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
    } catch (error) {
      console.warn("Failed to save to localStorage:", error);
    }
  }, []);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedData = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      const filteredData = {};
      SAVED_FIELDS.forEach((field) => {
        if (savedData[field]) {
          filteredData[field] = savedData[field];
        }
      });
      return filteredData;
    } catch (error) {
      console.warn("Failed to load from localStorage:", error);
      return {};
    }
  }, []);

  // Initialize state with localStorage data and initial overrides
  const [state, dispatch] = useReducer(propUpdateReducer, {
    scn_egg_count: "",
    female_index: "",
    state_name: "",
    county_name: "",
    commodity_name: "Soybeans", // Hard-coded as requested
    type_name: "",
    practice_name: "",
    map_unit_name: "",
    avg_sand: "",
    avg_soil_ph: "",
    yield_potential: "",
    projected_price: "",
    ...loadFromLocalStorage(), // Load saved values on initialization
    ...initialOverrides, // Apply any initial overrides
  });

  // State for API data
  const [ssurgo, setSsurgo] = useState([]);
  const [countyData, setCountyData] = useState({});
  const [price, setPrice] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSsurgoLoading, setIsSsurgoLoading] = useState(false);

  // Save to localStorage when saved fields change
  useEffect(() => {
    const fieldsToSave = {};
    SAVED_FIELDS.forEach((field) => {
      if (state[field] !== undefined && state[field] !== null && state[field] !== "") {
        fieldsToSave[field] = state[field];
      }
    });

    if (Object.keys(fieldsToSave).length > 0) {
      saveToLocalStorage(fieldsToSave);
    }
  }, [
    state.state_name,
    state.county_name,
    state.type_name,
    state.practice_name,
    state.scn_egg_count,
    state.map_unit_name,
    state.yield_potential,
    state.projected_price,
    state.female_index,
    state.avg_sand,
    state.avg_soil_ph,
    saveToLocalStorage,
  ]);

  // Calculate female index based on state - prioritize calculated value for cascading
  const calculatedFemaleIndex = state.state_name ? (FEMALE_INDEX_BY_STATE[state.state_name] ?? null) : null;
  const finalFemaleIndex = calculatedFemaleIndex !== null ? calculatedFemaleIndex : state.female_index || null;

  // Get soil data when map_unit_name is specified - prioritize calculated values for cascading
  const selectedMapUnit = state.map_unit_name
    ? ssurgo.find((unit) => unit.map_unit_name === state.map_unit_name)
    : null;
  const finalSoilSandAverage =
    selectedMapUnit?.avg_sand !== undefined ? selectedMapUnit?.avg_sand : state.avg_sand || null;
  const finalSoilPhAverage =
    selectedMapUnit?.avg_soil_ph !== undefined ? selectedMapUnit?.avg_soil_ph : state.avg_soil_ph || null;

  // Get yield potential and projected price - prioritize calculated values for cascading
  // If API data has been loaded (object exists), use API value even if null, otherwise use state value
  const finalYieldPotential =
    Object.keys(countyData).length > 0 ? countyData.expected_index_value : state.yield_potential;
  const finalProjectedPrice = Object.keys(price).length > 0 ? price.projected_price : state.projected_price;

  // Handle cascading updates when certain fields change
  const handleFieldChange = useCallback(
    (fieldName, value) => {
      if (fieldName === "state_name" && value !== state.state_name) {
        dispatch({
          type: "updateMany",
          payload: {
            state_name: value,
            county_name: "",
            type_name: "",
            practice_name: "",
            map_unit_name: "",
            avg_sand: "",
            avg_soil_ph: "",
            // Clear values that depend on state/county/type/practice
            yield_potential: "",
            projected_price: "",
            // Clear female_index since it depends on state
            female_index: "",
          },
        });
      } else if (fieldName === "county_name" && value !== state.county_name) {
        dispatch({
          type: "updateMany",
          payload: {
            county_name: value,
            type_name: "",
            practice_name: "",
            map_unit_name: "",
            avg_sand: "",
            avg_soil_ph: "",
            // Clear values that depend on county/type/practice
            yield_potential: "",
            projected_price: "",
          },
        });
      } else if (fieldName === "type_name" && value !== state.type_name) {
        dispatch({
          type: "updateMany",
          payload: {
            type_name: value,
            practice_name: "",
            // Clear values that depend on type/practice
            yield_potential: "",
            projected_price: "",
          },
        });
      } else if (fieldName === "practice_name" && value !== state.practice_name) {
        dispatch({
          type: "updateMany",
          payload: {
            practice_name: value,
            // Clear values that depend on practice
            yield_potential: "",
            projected_price: "",
          },
        });
      } else if (fieldName === "map_unit_name" && value !== state.map_unit_name) {
        dispatch({
          type: "updateMany",
          payload: {
            map_unit_name: value,
            // Clear soil values since they depend on map unit
            avg_sand: "",
            avg_soil_ph: "",
          },
        });
      } else {
        dispatch({
          type: "update",
          payload: { key: fieldName, value: value },
        });
      }
    },
    [state.state_name, state.county_name, state.type_name, state.practice_name, state.map_unit_name]
  );

  // Auto-populate yield_potential when hook provides new value - only when empty or source changes
  useEffect(() => {
    // Only update if the field is empty or if we just got new API data
    if (finalYieldPotential && (!state.yield_potential || state.yield_potential === "")) {
      dispatch({
        type: "update",
        payload: { key: "yield_potential", value: finalYieldPotential },
      });
    }
  }, [finalYieldPotential, countyData]);

  // Auto-populate projected_price when hook provides new value - only when empty or source changes
  useEffect(() => {
    // Only update if the field is empty or if we just got new API data
    if (finalProjectedPrice && (!state.projected_price || state.projected_price === "")) {
      dispatch({
        type: "update",
        payload: { key: "projected_price", value: finalProjectedPrice },
      });
    }
  }, [finalProjectedPrice, price]);

  // Auto-populate female_index when hook provides new value - ALWAYS update
  useEffect(() => {
    if (finalFemaleIndex !== null && finalFemaleIndex !== undefined) {
      dispatch({
        type: "update",
        payload: { key: "female_index", value: finalFemaleIndex },
      });
    }
  }, [finalFemaleIndex]);

  // Auto-populate soil fields when hook provides new values - ALWAYS update
  useEffect(() => {
    if (finalSoilSandAverage !== null && finalSoilPhAverage !== null) {
      const newAvgSand = finalSoilSandAverage || "";
      const newAvgSoilPh = finalSoilPhAverage || "";

      dispatch({
        type: "updateMany",
        payload: {
          avg_sand: newAvgSand,
          avg_soil_ph: newAvgSoilPh,
        },
      });
    }
  }, [finalSoilSandAverage, finalSoilPhAverage]);

  // Reset function
  const resetState = useCallback(() => {
    dispatch({
      type: "replace",
      payload: {
        commodity_name: "Soybeans", // Keep commodity hard-coded even on reset
      },
    });

    // Clear saved settings from localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear localStorage:", error);
    }
  }, []);

  // SSURGO data loading
  const loadSsurgoData = useCallback(
    async (filters) => {
      try {
        setIsSsurgoLoading(true);
        const response = await ApiHelper.callSmociObject(
          "data/DATANAC",
          "SSURGO",
          "GSSURGO",
          "SelectObject",
          filters,
          globalState
        );

        setSsurgo(response || []);
        return response || [];
      } catch (error) {
        console.error("Error loading SSURGO data:", error);
        setSsurgo([]);
        throw error;
      } finally {
        setIsSsurgoLoading(false);
      }
    },
    [globalState]
  );

  // SCN Risk Calculation Function
  const calculateScnSoybeanRisk = useCallback((calculationParams) => {
    const { scn_egg_count, female_index, avg_sand, avg_soil_ph, yield_potential, projected_price } = calculationParams;

    // Helper function to round to specified decimal places (exact copy from HTML)
    const round = (number, decimals, base) => {
      const precision = Math.pow(base ?? 10, decimals);
      return Math.round(number * precision) / precision;
    };

    // Calculate base yield loss (exact copy from HTML)
    const calculateBaseYieldLoss = (scnEggCount, femaleIndex) => {
      const eggImpact = (scnEggCount / 1000) * 0.22209;
      const femaleImpact1 = 0.10319 * femaleIndex;
      const femaleImpact2 = 0.199404 * femaleIndex * 0.039971 * 1.753;
      const yieldLoss = 100 * (1 - (68.6 - eggImpact - femaleImpact1 - femaleImpact2) / 68.6);
      return Math.max(0, yieldLoss);
    };

    // Calculate soil adjusted yield loss (exact copy from HTML)
    const calculateSoilAdjustedYieldLoss = (baseYieldLoss, sandPercentage, soilPh) => {
      const sandAdjustment = ((sandPercentage - 20) / 20) * 4.79758;
      const phAdjustment = 2.3281 * (soilPh - 7);
      const adjustedYieldLoss = baseYieldLoss + sandAdjustment + phAdjustment;
      return Math.max(0, adjustedYieldLoss);
    };

    // Calculate profit loss (exact copy from HTML)
    const calculateProfitLoss = (yieldLoss, yieldPotential, soybeanPrice) => {
      return yieldPotential * (yieldLoss / 100) * round(soybeanPrice, 2);
    };

    // Main calculation using exact user input values
    const baseYieldLoss = calculateBaseYieldLoss(scn_egg_count, female_index);
    const adjustedYieldLoss = calculateSoilAdjustedYieldLoss(baseYieldLoss, avg_sand, avg_soil_ph);
    const roundedYieldLoss = round(adjustedYieldLoss, 1);
    const profitLoss = calculateProfitLoss(roundedYieldLoss, yield_potential, projected_price);

    return {
      yield_loss_percentage: roundedYieldLoss,
      yield_loss_bushels: yield_potential * (roundedYieldLoss / 100),
      profit_loss: profitLoss,
    };
  }, []);

  // Field Validation Function
  const validateField = useCallback((fieldName, value) => {
    if (!value || value === "") return { isValid: true, error: null };

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return { isValid: false, error: "Must be a valid number" };

    switch (fieldName) {
      case "yield_potential":
        if (
          numericValue < VALIDATION_CONSTANTS.YIELD_POTENTIAL.min ||
          numericValue > VALIDATION_CONSTANTS.YIELD_POTENTIAL.max
        ) {
          return {
            isValid: false,
            error: `Must be between ${VALIDATION_CONSTANTS.YIELD_POTENTIAL.min} and ${VALIDATION_CONSTANTS.YIELD_POTENTIAL.max}`,
          };
        }
        break;
      case "projected_price":
        if (
          numericValue < VALIDATION_CONSTANTS.PROJECTED_PRICE.min ||
          numericValue > VALIDATION_CONSTANTS.PROJECTED_PRICE.max
        ) {
          return {
            isValid: false,
            error: `Must be between ${VALIDATION_CONSTANTS.PROJECTED_PRICE.min} and ${VALIDATION_CONSTANTS.PROJECTED_PRICE.max}`,
          };
        }
        break;
      case "female_index":
        if (
          numericValue < VALIDATION_CONSTANTS.FEMALE_INDEX.min ||
          numericValue > VALIDATION_CONSTANTS.FEMALE_INDEX.max
        ) {
          return {
            isValid: false,
            error: `Must be between ${VALIDATION_CONSTANTS.FEMALE_INDEX.min} and ${VALIDATION_CONSTANTS.FEMALE_INDEX.max}`,
          };
        }
        break;
      case "scn_egg_count":
        if (
          numericValue < VALIDATION_CONSTANTS.SCN_EGG_COUNT.min ||
          numericValue > VALIDATION_CONSTANTS.SCN_EGG_COUNT.max
        ) {
          return {
            isValid: false,
            error: `Must be between ${VALIDATION_CONSTANTS.SCN_EGG_COUNT.min} and ${VALIDATION_CONSTANTS.SCN_EGG_COUNT.max}`,
          };
        }
        break;
      case "avg_sand":
        if (
          numericValue < VALIDATION_CONSTANTS.AVERAGE_SAND.min ||
          numericValue > VALIDATION_CONSTANTS.AVERAGE_SAND.max
        ) {
          return {
            isValid: false,
            error: `Must be between ${VALIDATION_CONSTANTS.AVERAGE_SAND.min} and ${VALIDATION_CONSTANTS.AVERAGE_SAND.max}`,
          };
        }
        break;
      case "avg_soil_ph":
        if (
          numericValue < VALIDATION_CONSTANTS.AVERAGE_SOIL_PH.min ||
          numericValue > VALIDATION_CONSTANTS.AVERAGE_SOIL_PH.max
        ) {
          return {
            isValid: false,
            error: `Must be between ${VALIDATION_CONSTANTS.AVERAGE_SOIL_PH.min} and ${VALIDATION_CONSTANTS.AVERAGE_SOIL_PH.max}`,
          };
        }
        break;
      default:
        break;
    }

    return { isValid: true, error: null };
  }, []);

  // Auto-load county data when dependencies change
  useEffect(() => {
    const loadData = async () => {
      if (!state.state_name || !state.county_name || !state.type_name || !state.practice_name) {
        setCountyData({});
        return;
      }

      try {
        setIsLoading(true);
        const countyParameters = {
          reinsurance_year: String(getCurrentReinsuranceYear()),
          state_name: state.state_name,
          county_name: state.county_name,
          commodity_name: "Soybeans",
          type_name: state.type_name,
          practice_name: state.practice_name,
        };

        const _prices = await RmaApiHelper.selectCountyData(countyParameters);
        if (_prices?.length) {
          setCountyData(_prices[0]);
        } else {
          setCountyData({});
        }
      } catch (err) {
        setCountyData({});
        console.error("Error loading county data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [state.state_name, state.county_name, state.type_name, state.practice_name]);

  // Auto-load price data when dependencies change
  useEffect(() => {
    const loadData = async () => {
      if (!state.state_name || !state.county_name || !state.type_name || !state.practice_name) {
        setPrice({});
        return;
      }

      try {
        setIsLoading(true);
        const _currentCounty = {
          reinsurance_year: String(getCurrentReinsuranceYear()),
          state_name: state.state_name,
          county_name: state.county_name,
          commodity_name: "Soybeans",
          type_name: state.type_name,
          practice_name: state.practice_name,
        };

        let currentPrices = await RmaApiHelper.selectPrices(_currentCounty);
        if (!currentPrices?.length) {
          currentPrices = await RmaApiHelper.selectPrices({
            ..._currentCounty,
            reinsurance_year: String(getCurrentReinsuranceYear() - 1),
          });
          if (currentPrices?.length) {
            currentPrices[0].is_prior_year = true;
          }
        }

        if (currentPrices?.length) {
          setPrice(currentPrices[0]);
        } else {
          setPrice({});
        }
      } catch (err) {
        setPrice({});
        console.error("Error loading price data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [state.state_name, state.county_name, state.type_name, state.practice_name]);

  return {
    // Data
    ssurgo,
    countyData,
    price,
    femaleIndex: finalFemaleIndex,
    soil_sand_average: finalSoilSandAverage,
    soil_ph_average: finalSoilPhAverage,
    yield_potential: finalYieldPotential,
    projected_price: finalProjectedPrice,

    // Loading states
    isLoading,
    isSsurgoLoading,

    // State management
    state,
    dispatch,
    handleFieldChange,
    resetState,

    // Actions
    actions: {
      loadSsurgoData,
      calculateScnSoybeanRisk,
    },

    // Validation
    validateField,
  };
};
